<template>
  <div class="w-full min-h-screen flex flex-col">
    <MainNav :inverted="true" />

    <!-- Hero Section -->
    <section class="w-full relative">
      <img
        src="/image/83islands_location.jpg"
        alt="83 Islands Distillery"
        class="w-full h-[500px] object-cover object-center"
      />

      <div class="mx-auto absolute inset-0 z-20 w-full p-4 lg:p-36 flex flex-col items-center justify-center">
        <div class="max-w-4xl text-center">
          <h1 class="text-white font-maax text-4xl uppercase lg:text-6xl font-bold leading-tight drop-shadow-[0_5px_5px_rgba(0,0,0,0.5)] mb-4">
            Join Our Team
          </h1>
          <p class="text-white/90 text-xl font-maax max-w-2xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
            Be part of Vanuatu's first craft rum distillery
          </p>
        </div>
      </div>

      <!-- Double Wave -->
      <div class="absolute -bottom-1 left-0 right-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="w-full h-20 text-white">
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor" class="opacity-25"/>
          <path d="M985.66,92.83C906.67,72,823.78,31,743.84,14.19c-82.26-17.34-168.06-16.33-250.45.39-57.84,11.73-114,31.07-172,41.86A600.21,600.21,0,0,1,0,27.35V120H1200V95.8C1132.19,118.92,1055.71,111.31,985.66,92.83Z"
                fill="currentColor"/>
        </svg>
      </div>
    </section>

    <main class="flex-grow w-full bg-white">
      <!-- Job Listings -->
      <div class="max-w-4xl mx-auto px-4 py-12 lg:py-16">

        <div v-for="job in openJobs" :key="job.id" :id="job.id" class="mb-16">
          <div class="mb-8 pb-8 border-b border-gray-200">
            <div class="flex flex-wrap items-center gap-3 mb-4">
              <span class="inline-block px-3 py-1 bg-green-100 text-green-800 text-sm font-maax font-medium rounded-full">Open Position</span>
              <span v-if="job.deadline" class="text-sm text-gray-500 font-maax">Apply before {{ job.deadline }}</span>
            </div>
            <h2 class="text-3xl lg:text-4xl font-maax font-bold uppercase text-black mb-2">{{ job.title }}</h2>
            <div class="flex flex-wrap items-center gap-4 text-gray-600 font-maax">
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                {{ job.location }}
              </span>
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ job.type }}
              </span>
            </div>
          </div>

          <!-- Job Description -->
          <div class="prose prose-lg max-w-none font-maax">
            <p class="text-xl text-black leading-relaxed italic border-l-4 border-gold pl-6 py-4 mb-8">
              {{ job.summary }}
            </p>

            <div v-for="(section, sIndex) in job.sections" :key="sIndex" class="mb-8">
              <h3 class="text-2xl font-maax font-bold text-black mb-4">{{ section.title }}</h3>
              <ul class="space-y-2 text-lg text-black">
                <li v-for="(item, iIndex) in section.items" :key="iIndex" class="flex items-start gap-3">
                  <span class="text-gold mt-1 flex-shrink-0">&#9679;</span>
                  <span>{{ item }}</span>
                </li>
              </ul>
            </div>

            <!-- Share Section -->
            <div class="my-10 p-6 bg-gray-50 rounded-lg">
              <h3 class="text-lg font-maax font-bold text-black mb-3">Share this position</h3>
              <div class="flex flex-wrap gap-3">
                <a
                  :href="getShareUrl('facebook', job)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
                  Facebook
                </a>
                <a
                  :href="getShareUrl('x', job)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  X / Twitter
                </a>
                <a
                  :href="getShareUrl('linkedin', job)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
                >
                  <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
                <button
                  @click="copyJobLink(job)"
                  class="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
                  </svg>
                  {{ linkCopied === job.id ? 'Link Copied!' : 'Copy Link' }}
                </button>
              </div>
            </div>

            <!-- Application Form -->
            <div class="mt-12 pt-8 border-t border-gray-200">
              <h3 class="text-2xl font-maax font-bold uppercase text-black mb-6">Apply for this position</h3>

              <div v-if="submitSuccess === job.id" class="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg">
                <p class="text-green-800 font-maax text-lg font-medium">Thank you for your application!</p>
                <p class="text-green-700 font-maax mt-1">We have received your application and will review it shortly. We will contact you if your profile matches our requirements.</p>
              </div>

              <form v-else @submit.prevent="handleSubmit(job)" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label class="block font-maax text-lg font-medium text-gray-700">Full Name *</label>
                    <input
                      type="text"
                      v-model="form.name"
                      required
                      class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block font-maax text-lg font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      v-model="form.email"
                      required
                      class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block font-maax text-lg font-medium text-gray-700">Phone *</label>
                    <input
                      type="tel"
                      v-model="form.phone"
                      required
                      class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block font-maax text-lg font-medium text-gray-700">Location (Neighborhood)</label>
                    <input
                      type="text"
                      v-model="form.location"
                      placeholder="e.g., Tassiriki, Freshwota, Nambatu"
                      class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700">Languages spoken *</label>
                  <input
                    type="text"
                    v-model="form.languages"
                    placeholder="e.g., English, Bislama, French"
                    required
                    class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700">Relevant qualifications or experience *</label>
                  <textarea
                    v-model="form.qualifications"
                    rows="3"
                    required
                    placeholder="Describe your education, certifications, or relevant work experience"
                    class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  ></textarea>
                </div>

                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700">Do you have a driving or forklift licence?</label>
                  <input
                    type="text"
                    v-model="form.licence"
                    placeholder="e.g., Driving licence, Forklift licence, None"
                    class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700">Why are you interested in this role?</label>
                  <textarea
                    v-model="form.motivation"
                    rows="3"
                    placeholder="Tell us what motivates you to apply"
                    class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  ></textarea>
                </div>

                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700">Reference contact (optional)</label>
                  <input
                    type="text"
                    v-model="form.reference"
                    placeholder="Name and contact details of a reference"
                    class="mt-1 block w-full rounded-md bg-gray-50 border border-gray-300 px-3 py-2 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>

                <!-- File Uploads -->
                <div>
                  <label class="block font-maax text-lg font-medium text-gray-700 mb-1">Attach documents</label>
                  <p class="text-sm text-gray-500 mb-3">CV, cover letter, reference letters, or other relevant documents. Max 3 files, 4 MB each. Accepted: PDF, DOC, DOCX, JPG, PNG.</p>
                  <div class="space-y-3">
                    <div
                      class="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors cursor-pointer"
                      @click="$refs.fileInput.click()"
                      @dragover.prevent="dragOver = true"
                      @dragleave.prevent="dragOver = false"
                      @drop.prevent="handleFileDrop"
                      :class="{ 'border-blue-400 bg-blue-50': dragOver }"
                    >
                      <input
                        ref="fileInput"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        class="hidden"
                        @change="handleFileSelect"
                      />
                      <svg class="mx-auto h-10 w-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                      </svg>
                      <p class="mt-2 text-sm text-gray-600">
                        <span class="font-medium text-black">Click to upload</span> or drag and drop
                      </p>
                    </div>

                    <!-- File List -->
                    <div v-if="files.length > 0" class="space-y-2">
                      <div
                        v-for="(file, index) in files"
                        :key="index"
                        class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div class="flex items-center gap-3 min-w-0">
                          <svg class="w-5 h-5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                          </svg>
                          <span class="text-sm text-gray-700 truncate">{{ file.name }}</span>
                          <span class="text-xs text-gray-400 flex-shrink-0">{{ formatFileSize(file.size) }}</span>
                        </div>
                        <button
                          type="button"
                          @click="removeFile(index)"
                          class="ml-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <p v-if="fileError" class="text-sm text-red-600">{{ fileError }}</p>
                  </div>
                </div>

                <!-- Honeypot (hidden from humans, bots will fill it) -->
                <div class="absolute -left-[9999px]" aria-hidden="true">
                  <label for="website_url">Website</label>
                  <input type="text" id="website_url" v-model="form.honeypot" tabindex="-1" autocomplete="off" />
                </div>

                <!-- Cloudflare Turnstile -->
                <div v-if="turnstileSiteKey" ref="turnstileContainer" class="cf-turnstile" :data-sitekey="turnstileSiteKey" data-theme="light"></div>

                <div v-if="submitError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p class="text-red-800 font-maax">{{ submitErrorMessage }}</p>
                </div>

                <div class="flex justify-end">
                  <button
                    type="submit"
                    class="inline-flex justify-center rounded-md border border-transparent bg-black py-3 px-8 text-lg font-maax font-bold text-white uppercase shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    :disabled="isSubmitting"
                  >
                    {{ isSubmitting ? 'Sending...' : 'Submit Application' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- No open positions message -->
        <div v-if="openJobs.length === 0" class="text-center py-16">
          <h2 class="text-3xl font-maax font-bold text-black mb-4">No open positions at this time</h2>
          <p class="text-xl text-gray-600 font-maax mb-8">Check back soon or send us a spontaneous application.</p>
          <a href="mailto:hr@83islands.com" class="inline-flex justify-center rounded-md border-2 border-black bg-white py-3 px-8 text-lg font-maax font-bold text-black uppercase hover:bg-black hover:text-white transition-colors">
            Contact HR
          </a>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="bg-black text-white py-16">
        <div class="max-w-7xl mx-auto px-4 text-center">
          <h3 class="text-3xl font-maax font-bold mb-4">Life at 83 Islands</h3>
          <p class="text-xl mb-8 max-w-2xl mx-auto">
            Join a passionate team crafting exceptional spirits in one of the most beautiful places on earth.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link
              to="/team"
              class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
            >
              Meet the Team
            </router-link>
            <router-link
              to="/discover"
              class="font-maax px-8 py-3 uppercase font-bold transition-all rounded-lg border-2 text-white hover:bg-white hover:text-black hover:border-white text-center"
            >
              Discover 83 Islands
            </router-link>
          </div>
        </div>
      </div>
    </main>

    <Footer class="relative" />
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import MainNav from '../components/MainNav.vue'
import Footer from '../components/Footer.vue'
import axios from 'axios'

const route = useRoute()
const isSubmitting = ref(false)
const submitSuccess = ref(null)
const submitError = ref(false)
const submitErrorMessage = ref('')
const linkCopied = ref(null)
const dragOver = ref(false)
const files = ref([])
const fileError = ref('')
const turnstileContainer = ref(null)
const formLoadedAt = ref(null)

const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

const MAX_FILES = 3
const MAX_FILE_SIZE = 4 * 1024 * 1024 // 4 MB
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png'
]

const form = ref({
  name: '',
  email: '',
  phone: '',
  location: '',
  languages: '',
  qualifications: '',
  licence: '',
  motivation: '',
  reference: '',
  honeypot: ''
})

const api = axios.create({
  baseURL: import.meta.env.DEV
    ? ''
    : import.meta.env.API_URL?.replace(/\/+$/, ''),
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

// Job listings data
const openJobs = ref([
  {
    id: 'assistant-distiller-lab-assistant',
    title: 'Assistant Distiller / Lab Assistant',
    location: 'Stella Mare, Efate',
    type: 'Full-time',
    deadline: '31 March 2026',
    summary: 'Be part of both production and laboratory operations at 83 Islands Distillery.',
    sections: [
      {
        title: 'Your role',
        items: [
          'Assist with fermentation, distillation, and bottling',
          'Collect and test samples (fermentation, distillation, aging)',
          'Record production and laboratory data',
          'Maintain equipment and a clean, safe work environment'
        ]
      },
      {
        title: "We're looking for someone who is",
        items: [
          'Reliable and motivated',
          'Organized and detail-oriented',
          'Comfortable with physical work',
          'Curious and eager to learn'
        ]
      },
      {
        title: 'Qualifications',
        items: [
          'Degree in Biology, Chemistry, or related field',
          'Reference letter if possible',
          'Bilingual: English & Bislama (required)',
          'French is a plus',
          'Driving licence is a strong advantage'
        ]
      },
      {
        title: 'Compensation',
        items: [
          'Annual performance review with opportunities for salary progression',
          'Grow with us — clear path to take on more responsibility over time'
        ]
      }
    ]
  }
])

const baseUrl = 'https://83islands.com'

const getShareUrl = (platform, job) => {
  const jobUrl = `${baseUrl}/jobs#${job.id}`
  const text = `${job.title} at 83 Islands Distillery — ${job.summary} Apply now!`

  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(jobUrl)}`
    case 'x':
      return `https://x.com/intent/tweet?url=${encodeURIComponent(jobUrl)}&text=${encodeURIComponent(text)}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}`
    default:
      return '#'
  }
}

const copyJobLink = async (job) => {
  const jobUrl = `${baseUrl}/jobs#${job.id}`
  try {
    await navigator.clipboard.writeText(jobUrl)
    linkCopied.value = job.id
    setTimeout(() => { linkCopied.value = null }, 2000)
  } catch {
    const input = document.createElement('input')
    input.value = jobUrl
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    linkCopied.value = job.id
    setTimeout(() => { linkCopied.value = null }, 2000)
  }
}

// File handling
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

const validateFile = (file) => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return `"${file.name}" is not an accepted file type. Please use PDF, DOC, DOCX, JPG, or PNG.`
  }
  if (file.size > MAX_FILE_SIZE) {
    return `"${file.name}" is too large (${formatFileSize(file.size)}). Maximum size is 4 MB.`
  }
  return null
}

const addFiles = (newFiles) => {
  fileError.value = ''

  if (files.value.length + newFiles.length > MAX_FILES) {
    fileError.value = `You can upload a maximum of ${MAX_FILES} files.`
    return
  }

  for (const file of newFiles) {
    const error = validateFile(file)
    if (error) {
      fileError.value = error
      return
    }
    files.value.push(file)
  }
}

const handleFileSelect = (event) => {
  addFiles(Array.from(event.target.files))
  event.target.value = '' // Reset so same file can be re-selected
}

const handleFileDrop = (event) => {
  dragOver.value = false
  addFiles(Array.from(event.dataTransfer.files))
}

const removeFile = (index) => {
  files.value.splice(index, 1)
  fileError.value = ''
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // Remove the data:...;base64, prefix
      const base64 = reader.result.split(',')[1]
      resolve({ name: file.name, type: file.type, size: file.size, data: base64 })
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

const handleSubmit = async (job) => {
  if (isSubmitting.value) return
  submitError.value = false
  submitErrorMessage.value = ''

  // Honeypot check
  if (form.value.honeypot) {
    // Silently pretend success for bots
    submitSuccess.value = job.id
    return
  }

  // Time check — form filled in under 3 seconds is suspicious
  if (formLoadedAt.value && (Date.now() - formLoadedAt.value) < 3000) {
    submitSuccess.value = job.id
    return
  }

  // Get Turnstile token if available
  let turnstileToken = ''
  if (turnstileSiteKey) {
    const input = document.querySelector('[name="cf-turnstile-response"]')
    turnstileToken = input?.value || ''
    if (!turnstileToken) {
      submitError.value = true
      submitErrorMessage.value = 'Please complete the verification challenge before submitting.'
      return
    }
  }

  try {
    isSubmitting.value = true

    // Convert files to base64
    const encodedFiles = await Promise.all(files.value.map(fileToBase64))

    const applicationData = {
      jobTitle: job.title,
      jobId: job.id,
      name: form.value.name,
      email: form.value.email,
      phone: form.value.phone,
      location: form.value.location,
      languages: form.value.languages,
      qualifications: form.value.qualifications,
      licence: form.value.licence,
      motivation: form.value.motivation,
      reference: form.value.reference,
      files: encodedFiles,
      turnstileToken
    }

    const response = await api.post('/api/send-job-application', applicationData)

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to send application')
    }

    submitSuccess.value = job.id

    // Scroll to the success message
    await nextTick()
    const successEl = document.querySelector(`#${job.id} .bg-green-50`)
    if (successEl) {
      successEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // Reset form
    form.value = {
      name: '',
      email: '',
      phone: '',
      location: '',
      languages: '',
      qualifications: '',
      licence: '',
      motivation: '',
      reference: '',
      honeypot: ''
    }
    files.value = []
  } catch (error) {
    console.error('Error submitting application:', error)
    submitError.value = true
    submitErrorMessage.value = 'Sorry, there was an error submitting your application. Please try again or email us directly at hr@83islands.com.'
  } finally {
    isSubmitting.value = false
  }
}

// Set OG meta for social sharing (crawlers get these from api/jobs.js)
onMounted(() => {
  formLoadedAt.value = Date.now()

  // Set the first job title as the page OG title for client-side sharing
  const firstJob = openJobs.value[0]
  const ogTitle = firstJob
    ? `${firstJob.title} — 83 Islands Distillery is hiring`
    : 'Jobs at 83 Islands Distillery'
  const ogDescription = firstJob
    ? `${firstJob.summary} ${firstJob.location} — ${firstJob.type}. Apply before ${firstJob.deadline}.`
    : 'Join our team at Vanuatu\'s first craft rum distillery. View open positions and apply today.'

  const updateOrCreateMeta = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`)
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('property', property)
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', content)
  }

  document.title = ogTitle

  updateOrCreateMeta('og:title', ogTitle)
  updateOrCreateMeta('og:description', ogDescription)
  updateOrCreateMeta('og:image', `${baseUrl}/image/83islands_location.jpg`)
  updateOrCreateMeta('og:url', `${baseUrl}/jobs`)
  updateOrCreateMeta('og:type', 'website')
  updateOrCreateMeta('twitter:card', 'summary_large_image')
  updateOrCreateMeta('twitter:title', ogTitle)
  updateOrCreateMeta('twitter:description', ogDescription)
  updateOrCreateMeta('twitter:image', `${baseUrl}/image/83islands_location.jpg`)

  // Load Cloudflare Turnstile script if site key is configured
  if (turnstileSiteKey) {
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  // Scroll to job if hash present
  if (route.hash) {
    setTimeout(() => {
      const el = document.querySelector(route.hash)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    }, 300)
  }
})
</script>

<style scoped>
.text-gold {
  color: #C89B78;
}
.border-gold {
  border-color: #C89B78;
}
</style>
